import { UploadIcon } from '@/assets/SVGs/icons/UploadIcon';
import AppButton from '@/components/ui/AppButton';
import AppInput from '@/components/ui/AppInput';
import AppRadioButton from '@/components/ui/AppRadioButton';
import FormErrorMessage from '@/components/ui/FormErrorMessage';

import { endpoints } from '@/services/endpoints';
import { axiosGetRequest, axiosPostRequest } from '@/services/Request';
import { IBookData } from '@/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { forwardRef } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { toast, ToastContent } from 'react-toastify';
import * as z from 'zod';
import { X } from 'lucide-react';

type AddBookProps = Record<string, any>;

const AddBook = forwardRef<HTMLDialogElement, AddBookProps>((_, ref) => {
  const { data: allCategories } = useQuery({
    queryKey: [endpoints.getAllCategories],
    queryFn: () => axiosGetRequest(endpoints.getAllCategories),
    select: data => data?.result,
  });

  const { data: allSubCategories } = useQuery({
    queryKey: [endpoints.getAllSubCategory],
    queryFn: () => axiosGetRequest(endpoints.getAllSubCategory),
    select: data => data?.result,
  });

  const bookSchema = z.object({
    title: z.string().min(1, 'Title is required'),
    description: z.string().min(1, 'Description is required'),
    price: z.number(),
    images: z.any(),
    bookType: z.string().min(1, 'Post type is required'),
    category: z.string().min(1, 'Category is required'),
    subCategory: z.string().min(1, 'Sub-category is required'),
    bookEdition: z.number().positive('Edition must be a positive number'),
    bookCondition: z.string().min(1, 'Condition is required'),
    numberOfBooks: z.number().positive('Number of books must be a positive number'),
    educationTerm: z.string().min(1, 'Education term is required'),
    educationType: z.string().min(1, 'Education type is required'),
    location: z.string().min(1, 'Location is required'),
    city: z.string().min(1, 'City is required'),
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<IBookData>({
    resolver: zodResolver(bookSchema),
  });
  const selectedImages = watch('images');

  const { mutate: submitBook, isPending } = useMutation({
    mutationFn: (formData: FormData) => axiosPostRequest(`api/v2/books/add-book/`, formData),
    onError: (error: ToastContent) => {
      toast.error(error, {
        autoClose: false,
        style: { zIndex: 90000 },
      });
    },
    onSuccess: () => {
      toast.success('Book added successfully', {
        autoClose: false,
        style: { zIndex: 90000 },
      });
    },
  });
  const onSubmit: SubmitHandler<FieldValues> = async (data: FieldValues) => {
    const formData = new FormData();
    for (const key in data) {
      if (key === 'images') {
        Array.from(data.images).forEach((_, index) => formData.append(key, data.images[index]));
      } else {
        formData.append(key, data[key]);
      }
    }
    submitBook(formData);
  };

  return (
    <dialog
      ref={ref}
      className="fixed inset-0 z-50 m-auto w-full max-w-lg rounded-xl border border-coal-200 bg-white p-0 shadow-dropdown backdrop:bg-coal-900/40 backdrop:backdrop-blur-sm"
    >
      <div className="flex items-center justify-between border-b border-coal-200 px-6 py-4">
        <h2 className="text-lg font-semibold text-coal-900">Add Book</h2>
        <form method="dialog">
          <button
            type="submit"
            className="rounded-md p-1 text-coal-400 hover:bg-coal-100 hover:text-coal-600"
          >
            <X className="h-5 w-5" />
          </button>
        </form>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
        <div className="h-[calc(100svh-14rem)] space-y-4 overflow-auto px-6 py-4">
          {/* Title */}
          <div>
            <label htmlFor="title" className="mb-1.5 block text-sm font-medium text-coal-700">Book Title</label>
            <AppInput id="title" type="text" {...register('title')} />
            <FormErrorMessage message={errors.title?.message} />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="mb-1.5 block text-sm font-medium text-coal-700">Description</label>
            <AppInput id="description" type="text" {...register('description')} />
            <FormErrorMessage message={errors.description?.message} />
          </div>

          {/* Price */}
          <div>
            <label htmlFor="price" className="mb-1.5 block text-sm font-medium text-coal-700">Price</label>
            <AppInput id="price" type="text" {...register('price', { valueAsNumber: true })} />
            <FormErrorMessage message={errors.price?.message} />
          </div>

          {/* Images */}
          <div>
            <label htmlFor="images" className="mb-1.5 block text-sm font-medium text-coal-700">
              Images
            </label>
            <label htmlFor="images" className="cursor-pointer">
              <div className="flex flex-col items-center gap-2 rounded-lg border-2 border-dashed border-coal-300 p-5 transition-colors hover:border-primary hover:bg-primary-50">
                <UploadIcon height={32} width={32} className="text-primary" />
                <p className="text-sm text-coal-500">
                  {selectedImages?.length == 0 ? 'Only 5 images allowed' : `${selectedImages?.length} images selected`}
                </p>
              </div>
            </label>
            <input
              multiple
              accept="image/*"
              id="images"
              type="file"
              {...register('images')}
              className="hidden"
            />
            <FormErrorMessage message={errors.images?.message} />
          </div>

          {/* Post Type */}
          <div>
            <p className="mb-1.5 text-sm font-medium text-coal-700">Post type</p>
            <div className="flex gap-5">
              <div className="flex items-center gap-2">
                <AppRadioButton
                  value={'offered'}
                  type="radio"
                  id="offered"
                  {...register('bookType')}
                  onChange={e => setValue('bookType', e.target.value)}
                />
                <label htmlFor="offered" className="cursor-pointer text-sm text-coal-600">offered</label>
              </div>
              <div className="flex items-center gap-2">
                <AppRadioButton
                  value={'requested'}
                  type="radio"
                  id="requested"
                  {...register('bookType')}
                  onChange={e => setValue('bookType', e.target.value)}
                />
                <label htmlFor="requested" className="cursor-pointer text-sm text-coal-600">requested</label>
              </div>
            </div>
            <FormErrorMessage message={errors.bookType?.message} />
          </div>

          {/* Category */}
          <div>
            <label htmlFor="category" className="mb-1.5 block text-sm font-medium text-coal-700">Category</label>
            <select
              {...register('category')}
              className="h-9 w-full rounded-lg border border-coal-300 bg-white px-3 text-sm text-coal-900 transition-colors hover:border-coal-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="">Select a category</option>
              {allCategories?.map((category: any) => (
                <option key={category._id} value={category._id}>
                  {category.name_en || category.name_ar || category._id}
                </option>
              ))}
            </select>
            <FormErrorMessage message={errors.category?.message} />
          </div>

          {/* Sub-Category */}
          <div>
            <label htmlFor="subCategory" className="mb-1.5 block text-sm font-medium text-coal-700">Sub-Category</label>
            <select
              {...register('subCategory')}
              className="h-9 w-full rounded-lg border border-coal-300 bg-white px-3 text-sm text-coal-900 transition-colors hover:border-coal-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="">Select a sub-category</option>
              {allSubCategories
                ?.filter((sub: any) => {
                  const selectedCat = watch('category');
                  if (!selectedCat) return true;
                  const catId = typeof sub.category === 'object' ? sub.category?._id : sub.category;
                  return catId === selectedCat;
                })
                .map((sub: any) => (
                <option key={sub._id} value={sub._id}>
                  {sub.name_en || sub.name_ar || sub._id}
                  {sub.category?.name_en ? ` (${sub.category.name_en})` : ''}
                </option>
              ))}
            </select>
            <FormErrorMessage message={errors.subCategory?.message} />
          </div>

          {/* Book Edition */}
          <div>
            <label htmlFor="bookEdition" className="mb-1.5 block text-sm font-medium text-coal-700">Book Edition</label>
            <AppInput id="bookEdition" type="text" {...register('bookEdition', { valueAsNumber: true })} />
            <FormErrorMessage message={errors.bookEdition?.message} />
          </div>

          {/* Book Condition */}
          <div>
            <p className="mb-1.5 text-sm font-medium text-coal-700">Book Condition</p>
            <div className="flex gap-5">
              <div className="flex items-center gap-2">
                <AppRadioButton
                  value={'new'}
                  type="radio"
                  id="new"
                  {...register('bookCondition')}
                  onChange={e => setValue('bookCondition', e.target.value)}
                />
                <label htmlFor="new" className="cursor-pointer text-sm text-coal-600">new</label>
              </div>
              <div className="flex items-center gap-2">
                <AppRadioButton
                  value={'used'}
                  type="radio"
                  id="used"
                  {...register('bookCondition')}
                  onChange={e => setValue('bookCondition', e.target.value)}
                />
                <label htmlFor="used" className="cursor-pointer text-sm text-coal-600">used</label>
              </div>
            </div>
            <FormErrorMessage message={errors.bookCondition?.message} />
          </div>

          {/* Number of Books */}
          <div>
            <label htmlFor="numberOfBooks" className="mb-1.5 block text-sm font-medium text-coal-700">Number of Books</label>
            <AppInput id="numberOfBooks" type="text" {...register('numberOfBooks', { valueAsNumber: true })} />
            <FormErrorMessage message={errors.numberOfBooks?.message} />
          </div>

          {/* Education Term */}
          <div>
            <p className="mb-1.5 text-sm font-medium text-coal-700">Select Term</p>
            <div className="flex gap-5">
              <div className="flex items-center gap-2">
                <AppRadioButton
                  value={'first'}
                  type="radio"
                  id="first"
                  {...register('educationTerm')}
                  onChange={e => setValue('educationTerm', e.target.value)}
                />
                <label htmlFor="first" className="cursor-pointer text-sm text-coal-600">First</label>
              </div>
              <div className="flex items-center gap-2">
                <AppRadioButton
                  value={'second'}
                  type="radio"
                  id="second"
                  {...register('educationTerm')}
                  onChange={e => setValue('educationTerm', e.target.value)}
                />
                <label htmlFor="second" className="cursor-pointer text-sm text-coal-600">Second</label>
              </div>
            </div>
            <FormErrorMessage message={errors.educationTerm?.message} />
          </div>

          {/* Education Type */}
          <div>
            <p className="mb-1.5 text-sm font-medium text-coal-700">Select Education type</p>
            <div className="flex gap-5">
              <div className="flex items-center gap-2">
                <AppRadioButton
                  value={'azhar'}
                  type="radio"
                  id="azhar"
                  {...register('educationType')}
                  onChange={e => setValue('educationType', e.target.value)}
                />
                <label htmlFor="azhar" className="cursor-pointer text-sm text-coal-600">azhar</label>
              </div>
              <div className="flex items-center gap-2">
                <AppRadioButton
                  value={'general'}
                  type="radio"
                  id="general"
                  {...register('educationType')}
                  onChange={e => setValue('educationType', e.target.value)}
                />
                <label htmlFor="general" className="cursor-pointer text-sm text-coal-600">general</label>
              </div>
            </div>
            <FormErrorMessage message={errors.educationType?.message} />
          </div>

          {/* Location */}
          <div>
            <label htmlFor="location" className="mb-1.5 block text-sm font-medium text-coal-700">Location</label>
            <AppInput id="location" type="text" {...register('location')} />
            <FormErrorMessage message={errors.location?.message} />
          </div>

          {/* City */}
          <div>
            <label htmlFor="city" className="mb-1.5 block text-sm font-medium text-coal-700">City</label>
            <AppInput id="city" type="text" {...register('city')} />
            <FormErrorMessage message={errors.city?.message} />
          </div>
        </div>
        {/* Submit Button */}
        <div className="border-t border-coal-200 px-6 py-4">
          <AppButton loading={isPending} size="md" type="submit" fullWidth>
            Submit
          </AppButton>
        </div>
      </form>
    </dialog>
  );
});

AddBook.displayName = 'AddBook';

export default AddBook;
